import { FC, useState, useEffect, useRef } from 'react';
import { Link, Navigate, useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import FollowButton from '@/components/FollowButton';
import { profileService } from '@/services/profile.service';
import type { PublicProfileData, ProfileBadge } from '@/types/profile';

const RARITY_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  COMMON: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Comum' },
  RARE: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Raro' },
  EPIC: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', label: 'Épico' },
  LEGENDARY: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Lendário' },
};

const BadgeCard: FC<{ badge: ProfileBadge; highlight?: boolean }> = ({ badge, highlight }) => {
  const style = RARITY_STYLES[badge.rarity] || RARITY_STYLES.COMMON;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${style.border} ${style.bg} ${highlight ? 'ring-1 ring-amber-500/40' : ''}`}>
      {badge.icon_url ? (
        <img src={badge.icon_url} alt={badge.title} className="size-10 rounded-lg object-cover" />
      ) : (
        <div className={`size-10 rounded-lg flex items-center justify-center ${style.bg}`}>
          <span className={`material-symbols-outlined ${style.text}`}>{badge.icon || 'emoji_events'}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-content-primary truncate">{badge.title}</p>
        <p className={`text-xs font-medium ${style.text}`}>{style.label}</p>
      </div>
    </div>
  );
};

const PublicProfilePage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const { user: authUser } = useAuthContext();
  const isOwnProfile = !!(authUser && userId === authUser.id);
  const [data, setData] = useState<PublicProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [followersCount, setFollowersCount] = useState(0);

  // Fetch the public profile data
  useEffect(() => {
    if (!userId || isOwnProfile) return;

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const profileData = await profileService.getPublicProfile(userId);

        if (cancelled) return;

        // Server-side check: redirect to own profile
        if (profileData.is_own_profile) {
          navigateRef.current('/perfil', { replace: true });
          return;
        }

        setData(profileData);
        setFollowersCount(profileData.followers_count);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar perfil');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchProfile();

    return () => { cancelled = true; };
  }, [userId, isOwnProfile]);

  // Client-side redirect: if viewing own profile
  if (isOwnProfile) {
    return <Navigate to="/perfil" replace />;
  }

  const handleFollowChange = (isFollowing: boolean) => {
    setFollowersCount(prev => isFollowing ? prev + 1 : prev - 1);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">Erro ao carregar perfil: {error}</p>
      </div>
    );
  }

  const { user } = data;

  // Private profile view
  if (!data.is_public) {
    return (
      <div className="flex flex-1 justify-center py-8 bg-surface-primary">
        <div className="flex flex-col max-w-[900px] flex-1 px-4 md:px-10 gap-8">
          <section className="flex flex-col items-center gap-6 p-8 bg-surface-card border border-theme-border rounded-xl">
            {user.avatar ? (
              <img src={user.avatar} alt={user.name} className="size-24 rounded-full object-cover border-2 border-theme-border" />
            ) : (
              <div className="size-24 rounded-full bg-accent/10 flex items-center justify-center border-2 border-theme-border">
                <span className="material-symbols-outlined text-4xl text-accent">person</span>
              </div>
            )}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-content-primary">{user.name}</h1>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2 text-sm text-content-muted">
                {user.squad && (
                  <span className="flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">groups</span>
                    {user.squad.name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">military_tech</span>
                  Nível {user.level} — {user.level_name}
                </span>
              </div>
              <p className="text-sm text-content-muted mt-2">
                {followersCount} {followersCount === 1 ? 'pessoa acompanha' : 'pessoas acompanham'} este perfil
              </p>
            </div>
            <FollowButton
              userId={user.id}
              initialIsFollowing={data.is_following}
              onFollowChange={handleFollowChange}
            />
            <div className="flex items-center gap-3 p-4 mt-2 bg-surface-tertiary/50 rounded-xl border border-theme-border" data-testid="private-profile-card">
              <span className="material-symbols-outlined text-2xl text-content-muted">lock</span>
              <p className="text-sm text-content-muted">Este perfil é privado</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Public profile view
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
    : '';

  const xpProgress = (user.next_level_xp ?? 0) > 0
    ? (((user.next_level_xp ?? 0) - (user.xp_to_next_level ?? 0)) / (user.next_level_xp ?? 1)) * 100
    : 100;

  const recent_badges = data.recent_badges ?? [];
  const rarest_badge = data.rarest_badge ?? null;
  const total_badges = data.total_badges ?? 0;
  const courses_in_progress = data.courses_in_progress ?? [];
  const courses_completed = data.courses_completed ?? [];
  const season_history = data.season_history ?? [];
  const ranking_position = data.ranking_position;
  const season_xp = data.season_xp ?? 0;
  const season_name = data.season_name ?? '';

  return (
    <div className="flex flex-1 justify-center py-8 bg-surface-primary">
      <div className="flex flex-col max-w-[900px] flex-1 px-4 md:px-10 gap-8">

        {/* Back to Ranking */}
        <Link
          to="/ranking"
          className="flex items-center gap-1 text-sm text-content-muted hover:text-content-primary transition-colors w-fit"
          data-testid="back-to-ranking"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Voltar ao Ranking
        </Link>

        {/* Header */}
        <section className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 bg-surface-card border border-theme-border rounded-xl">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="size-24 rounded-full object-cover border-2 border-theme-border shrink-0" />
          ) : (
            <div className="size-24 rounded-full bg-accent/10 flex items-center justify-center border-2 border-theme-border shrink-0">
              <span className="material-symbols-outlined text-4xl text-accent">person</span>
            </div>
          )}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold text-content-primary">{user.name}</h1>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 mt-1 text-sm text-content-muted">
              {user.squad && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">groups</span>
                  {user.squad.name}
                </span>
              )}
              {user.sector && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">apartment</span>
                  {typeof user.sector === 'string' ? user.sector : user.sector.name}
                </span>
              )}
              {memberSince && (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-base">calendar_month</span>
                  Membro desde {memberSince}
                </span>
              )}
            </div>
            {user.bio && <p className="text-sm text-content-secondary mt-2">{user.bio}</p>}
            <p className="text-sm text-content-muted mt-2">
              {followersCount} {followersCount === 1 ? 'pessoa acompanha' : 'pessoas acompanham'} este perfil
            </p>
          </div>
          <div className="shrink-0">
            <FollowButton
              userId={user.id}
              initialIsFollowing={data.is_following}
              onFollowChange={handleFollowChange}
            />
          </div>
        </section>

        {/* Level */}
        <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
          <div className="flex items-center gap-4 mb-4">
            {user.level_icon_url ? (
              <img src={user.level_icon_url} alt={user.level_name} className="size-12 rounded-lg object-cover" />
            ) : (
              <div className="size-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-accent">military_tech</span>
              </div>
            )}
            <div>
              <p className="text-lg font-bold text-content-primary">Nível {user.level} — {user.level_name}</p>
              <p className="text-sm text-content-muted">
                {(user.xp_to_next_level ?? 0) > 0
                  ? `${(user.xp_to_next_level ?? 0).toLocaleString('pt-BR')} XP para o próximo nível`
                  : 'Nível máximo atingido!'}
              </p>
            </div>
          </div>
          <div className="w-full h-3 bg-surface-tertiary rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500"
              style={{ width: `${Math.min(xpProgress, 100)}%` }}
            />
          </div>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-accent mb-1">stars</span>
            <span className="text-xl font-bold text-content-primary">{(user.total_xp ?? 0).toLocaleString('pt-BR')}</span>
            <span className="text-xs text-content-muted">XP Total</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-blue-400 mb-1">bolt</span>
            <span className="text-xl font-bold text-content-primary">{season_xp.toLocaleString('pt-BR')}</span>
            <span className="text-xs text-content-muted">{season_name ? `XP ${season_name}` : 'XP Temporada'}</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-amber-400 mb-1">leaderboard</span>
            <span className="text-xl font-bold text-content-primary">{ranking_position ? `#${ranking_position}` : '—'}</span>
            <span className="text-xs text-content-muted">Ranking Geral</span>
          </div>
          <div className="flex flex-col items-center p-4 bg-surface-card border border-theme-border rounded-xl">
            <span className="material-symbols-outlined text-2xl text-orange-400 mb-1">local_fire_department</span>
            <span className="text-xl font-bold text-content-primary">{user.streak ?? 0}</span>
            <span className="text-xs text-content-muted">Dias de Streak</span>
          </div>
        </section>

        {/* Badges */}
        {total_badges > 0 && (
          <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
            <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-accent">emoji_events</span>
              Conquistas
              <span className="text-sm font-normal text-content-muted">({total_badges})</span>
            </h2>
            <div className="flex flex-col gap-3">
              {rarest_badge && !recent_badges.some(b => b.id === rarest_badge.id) && (
                <div>
                  <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2">Mais Rara</p>
                  <BadgeCard badge={rarest_badge} highlight />
                </div>
              )}
              {recent_badges.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2">Recentes</p>
                  <div className="flex flex-col gap-2">
                    {recent_badges.map((badge) => (
                      <BadgeCard key={badge.id} badge={badge} highlight={rarest_badge?.id === badge.id} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Courses */}
        {(courses_in_progress.length > 0 || courses_completed.length > 0) && (
          <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
            <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-accent">school</span>
              Cursos
            </h2>

            {courses_in_progress.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-3">Em Progresso</p>
                <div className="flex flex-col gap-3">
                  {courses_in_progress.map((course) => (
                    <Link
                      key={course.id}
                      to={`/trilhas/${course.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg border border-theme-border hover:border-accent/30 transition-all"
                    >
                      {course.thumbnail ? (
                        <img src={course.thumbnail} alt={course.title} className="size-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="size-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-accent">route</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-content-primary truncate">{course.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                            <div className="h-full bg-accent rounded-full" style={{ width: `${course.progress_percentage}%` }} />
                          </div>
                          <span className="text-xs text-content-muted shrink-0">{course.progress_percentage}%</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {courses_completed.length > 0 && (
              <div>
                <p className="text-xs font-bold text-content-muted uppercase tracking-wider mb-3">Concluídos</p>
                <div className="flex flex-col gap-3">
                  {courses_completed.map((course) => (
                    <Link
                      key={course.id}
                      to={`/trilhas/${course.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg border border-theme-border hover:border-green-500/30 transition-all"
                    >
                      {course.thumbnail ? (
                        <img src={course.thumbnail} alt={course.title} className="size-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="size-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-green-500">check_circle</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-content-primary truncate">{course.title}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-green-400 font-medium">{course.xp_earned} XP</span>
                          {course.completed_at && (
                            <span className="text-xs text-content-muted">
                              {new Date(course.completed_at).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Season History */}
        {season_history.length > 0 && (
          <section className="p-6 bg-surface-card border border-theme-border rounded-xl">
            <h2 className="text-lg font-bold text-content-primary flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-accent">history</span>
              Historico de Temporadas
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="text-left py-2 px-3 text-content-muted font-medium">Temporada</th>
                    <th className="text-center py-2 px-3 text-content-muted font-medium">Posição</th>
                    <th className="text-right py-2 px-3 text-content-muted font-medium">XP</th>
                  </tr>
                </thead>
                <tbody>
                  {season_history.map((entry) => (
                    <tr key={entry.season_id} className="border-b border-theme-border/50 last:border-0">
                      <td className="py-2.5 px-3 font-medium text-content-primary">{entry.season_name}</td>
                      <td className="py-2.5 px-3 text-center">
                        {entry.final_position ? (
                          <span className={`font-bold ${entry.final_position <= 3 ? 'text-amber-400' : 'text-content-primary'}`}>
                            #{entry.final_position}
                          </span>
                        ) : (
                          <span className="text-content-muted">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right text-content-secondary">{entry.total_xp.toLocaleString('pt-BR')} XP</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PublicProfilePage;
